import type { EnrichedPlayer } from '@hooks/transfers/usePlayerEnrichment';
import Icon from '@components/atoms/icon';

const PROGRESS_RISE_THRESHOLD = 0.75;
const PROGRESS_FALL_THRESHOLD = -0.75;

type PriceChangeIndicatorProps = {
  priceChange: EnrichedPlayer['priceChange'];
};

/**
 * Up/down arrow shown when a player is close to a price change.
 *
 * The `progress` field is signed: positive values track progress towards
 * a price rise, negative values track a price fall. The thresholds below
 * are deliberately conservative so we only highlight imminent changes.
 *
 * `estimatedChangeTime === 'Locked'` means FPL won't change this player's
 * price for the night, regardless of progress, so we suppress the badge.
 */
export const PriceChangeIndicator = ({
  priceChange
}: PriceChangeIndicatorProps) => {
  if (!priceChange) return null;
  if (priceChange.estimatedChangeTime === 'Locked') return null;

  const progress = priceChange.progress;

  if (progress >= PROGRESS_RISE_THRESHOLD) {
    return (
      <Icon
        name="ArrowUpward"
        className="w-3.5 h-3.5 text-success-700"
        data-cy="price-change-rise"
        title={`Likely to rise (${priceChange.estimatedChangeTime ?? 'soon'})`}
      />
    );
  }

  if (progress <= PROGRESS_FALL_THRESHOLD) {
    return (
      <Icon
        name="ArrowDownward"
        className="w-3.5 h-3.5 text-danger-700"
        data-cy="price-change-fall"
        title={`Likely to fall (${priceChange.estimatedChangeTime ?? 'soon'})`}
      />
    );
  }

  return null;
};
